// Is Dagilimlari sayfasi ana hook'u. 3 modda calisir:
//   'teams'   — Multi-team karsilastirma. Her ekip icin: kisi sayisi + iss
//               turu/durumu dagilimi.
//   'members' — Bir ekibi sec, uyelerinin isslerini kisi kisi karsilastir.
//   'people'  — Multi-user karsilastirma (ekip bagi olmadan, kisi bazli).
//
// Backend'e TEK bir jobs sorgusu atar (secim seti icin) → client-side gruplar.
// Bu, aggregate query'ler icin ayri ayri Hasura roundtrip'i yerine daha
// verimli (dagilim sayfasi total is sayisi kucuk, ~100-1000 kayit).

import { useCallback, useEffect, useMemo, useState } from 'react'; // eslint-disable-line no-unused-vars
import { hasuraClient } from '../utils/graphqlClient';
import { useAuth } from './useReduxAuth';
import {
  GET_TEAMS_LITE_FOR_DISTRIBUTION,
  GET_TEAM_USERS_LITE,
  GET_USERS_LITE_FOR_DISTRIBUTION,
  GET_JOBS_FOR_DISTRIBUTION,
} from '../graphql/queries/jobDistribution';

// Dagilimda sayilmayan statuler: is bitmis ya da dusmus demektir, ekibin
// GUNCEL yukunu gostermezler.
//
// YAZIMDA BURADA DEGIL: yazim da suren bir is ve kisinin uzerinde yuk
// yaratiyor — dagilimda gorunmesi gerekiyor. (Bir donem gizlenmisti,
// istek uzerine geri acildi.)
const HIDDEN_STATUSES = ['REDDEDILDI', 'KAPATILDI', 'TAMAMLANDI', 'DANISMANLIK_SONLANDIRILDI', 'ORTAK_BULUNDU'];

const jobsWhereForActiveStatuses = () => ({
  job_status: { _nin: HIDDEN_STATUSES },
});

// User kumesi uzerinden isler (members/people mode).
const jobsWhereForUsers = (userIds) => ({
  _and: [
    jobsWhereForActiveStatuses(),
    {
      job_assignment: {
        _or: [
          { technical_user_id: { _in: userIds } },
          { financial_user_id: { _in: userIds } },
        ],
      },
    },
  ],
});

// TEAM kumesi uzerinden isler (teams mode) — DORT dal OR'lanir:
//   1) technical_team_id / financial_team_id → is EKIBE atanmis
//   2) technical_user.team_id / financial_user.team_id → is KISIYE atanmis
//      ve o kisi bu ekibin uyesi
const jobsWhereForTeams = (teamIds) => ({
  _and: [
    jobsWhereForActiveStatuses(),
    {
      job_assignment: {
        _or: [
          { technical_team_id: { _in: teamIds } },
          { financial_team_id: { _in: teamIds } },
          { technical_user: { team_id: { _in: teamIds } } },
          { financial_user:  { team_id: { _in: teamIds } } },
        ],
      },
    },
  ],
});

// Bir isin `entityId`'ye "ait" olup olmadigini kontrol eder. jobs.job_assignment
// object relationship (tekil, .some kullanmiyoruz). team modunda 4-dalli OR:
// team_id direkt esitligi + user.team_id (kisiye atanmis ama team_id NULL).
const jobBelongsToEntity = (job, entityKind, entityId) => {
  const a = job.job_assignment;
  if (!a) return false;
  if (entityKind === 'team') {
    return (
      a.technical_team_id === entityId ||
      a.financial_team_id === entityId ||
      a.technical_user?.team_id === entityId ||
      a.financial_user?.team_id  === entityId
    );
  }
  // user
  return (
    a.technical_user_id === entityId ||
    a.financial_user_id === entityId
  );
};

// Job type label icin FK rel > legacy field fallback. Boslarsa 'Diger'.
const resolveJobType = (job) => job?.job_type_rel?.name || job?.job_type || 'DIGER';
const resolveJobStatus = (job) => job?.job_status || 'BILINMIYOR';

// Isten ayrilmis mi: users.work_end_date dolu ve bugun ya da gecmiste
// (useJobAssignments / JobAssignmentModal ile ayni olcut).
const isDeparted = (u) => {
  if (!u?.work_end_date) return false;
  const end = new Date(u.work_end_date);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return end <= today;
};

// Client-side aggregate helper.
const countBy = (items, resolver) => {
  const map = {};
  for (const it of items) {
    const key = resolver(it);
    map[key] = (map[key] || 0) + 1;
  }
  return map;
};

export const useJobDistribution = () => {
  const { user } = useAuth();
  const companyId = user?.companyId;

  // Mode + selection state
  const [mode, setMode] = useState(null); // null | 'teams' | 'members' | 'people'
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);   // teams mode
  const [selectedTeamId,  setSelectedTeamId]  = useState(null); // members mode
  const [selectedUserIds, setSelectedUserIds] = useState([]);   // people mode

  // Lookup listeleri
  const [teams, setTeams]         = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // Sonuc
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Teams + all users lookup — sayfa ilk acildiginda.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoadingLookups(true);
    Promise.all([
      hasuraClient.query(GET_TEAMS_LITE_FOR_DISTRIBUTION, { companyId }),
      hasuraClient.query(GET_USERS_LITE_FOR_DISTRIBUTION, { companyId }),
    ]).then(([teamsRes, usersRes]) => {
      if (cancelled) return;
      setTeams(teamsRes?.teams || []);
      setAllUsers(usersRes?.users || []);
    }).finally(() => { if (!cancelled) setLoadingLookups(false); });
    return () => { cancelled = true; };
  }, [companyId]);

  // Members mode: secilen ekibin uyelerini cek.
  useEffect(() => {
    if (mode !== 'members' || !selectedTeamId) { setTeamUsers([]); return; }
    let cancelled = false;
    hasuraClient.query(GET_TEAM_USERS_LITE, { teamId: selectedTeamId })
      .then(res => { if (!cancelled) setTeamUsers(res?.users || []); })
      .catch(() => { if (!cancelled) setTeamUsers([]); });
    return () => { cancelled = true; };
  }, [mode, selectedTeamId]);

  // Jobs fetch — mode'a gore where:
  //   teams   → jobsWhereForTeams (technical/financial_team_id _in)
  //   members → jobsWhereForUsers (ekipteki user_id'ler)
  //   people  → jobsWhereForUsers (secilen user_id'ler)
  //
  // teams'ta user_id degil team_id filter cunku is ekibe atanmis olabilir
  // ama kisi_id NULL olabiliyor (bu durumda sayilmasi lazim).
  useEffect(() => {
    let where = null;
    if (mode === 'teams' && selectedTeamIds.length > 0) {
      where = jobsWhereForTeams(selectedTeamIds);
    } else if (mode === 'members' && selectedTeamId && teamUsers.length > 0) {
      where = jobsWhereForUsers(teamUsers.map(u => u.id));
    } else if (mode === 'people' && selectedUserIds.length > 0) {
      where = jobsWhereForUsers(selectedUserIds);
    }
    if (!where) { setJobs([]); return; }

    let cancelled = false;
    setLoadingJobs(true);
    hasuraClient.query(GET_JOBS_FOR_DISTRIBUTION, { where })
      .then(res => { if (!cancelled) setJobs(res?.jobs || []); })
      .catch(() => { if (!cancelled) setJobs([]); })
      .finally(() => { if (!cancelled) setLoadingJobs(false); });
    return () => { cancelled = true; };
  }, [mode, selectedTeamIds, selectedTeamId, teamUsers, selectedUserIds]);

  // Karsilastirma cikti: her entity icin { entity, personCount, byType, byStatus, total }
  const distribution = useMemo(() => {
    if (mode === 'teams') {
      return selectedTeamIds.map(teamId => {
        const team = teams.find(t => t.id === teamId);
        const teamJobs = jobs.filter(j => jobBelongsToEntity(j, 'team', teamId));
        return {
          key: `t-${teamId}`,
          entity: { kind: 'team', id: teamId, label: team?.name || `#${teamId}` },
          personCount: team?.users_aggregate?.aggregate?.count ?? null,
          byType:   countBy(teamJobs, resolveJobType),
          byStatus: countBy(teamJobs, resolveJobStatus),
          total:    teamJobs.length,
        };
      });
    }
    if (mode === 'members' && selectedTeamId) {
      return teamUsers.map(u => {
        const userJobs = jobs.filter(j => jobBelongsToEntity(j, 'user', u.id));
        const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || `#${u.id}`;
        return {
          key: `u-${u.id}`,
          entity: { kind: 'user', id: u.id, label },
          personCount: null,
          byType:   countBy(userJobs, resolveJobType),
          byStatus: countBy(userJobs, resolveJobStatus),
          total:    userJobs.length,
          departed: isDeparted(u),
        };
      })
      // Ibrahim/Baris (2026-10-26): isten ayrilmis ve uzerinde is gorunmeyen
      // personel ekip ici dagilimda listelenmesin. Uzerinde is VARSA
      // (ayrilmis olsa da) yuk gercek oldugu icin gorunmeye devam eder.
      .filter(row => !(row.departed && row.total === 0));
    }
    if (mode === 'people') {
      return selectedUserIds.map(uid => {
        const u = allUsers.find(x => x.id === uid);
        const userJobs = jobs.filter(j => jobBelongsToEntity(j, 'user', uid));
        const label = u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : `#${uid}`;
        return {
          key: `u-${uid}`,
          entity: { kind: 'user', id: uid, label: label || `#${uid}`, teamName: u?.team?.name || null },
          personCount: null,
          byType:   countBy(userJobs, resolveJobType),
          byStatus: countBy(userJobs, resolveJobStatus),
          total:    userJobs.length,
        };
      });
    }
    return [];
  }, [mode, selectedTeamIds, teams, jobs, selectedTeamId, teamUsers, selectedUserIds, allUsers]);

  const resetSelection = useCallback(() => {
    setSelectedTeamIds([]);
    setSelectedTeamId(null);
    setSelectedUserIds([]);
    setTeamUsers([]);
    setJobs([]);
  }, []);

  return {
    // Mode
    mode, setMode,
    resetSelection,

    // Lookup listeleri
    teams, allUsers, teamUsers,
    loadingLookups,

    // Secim state
    selectedTeamIds, setSelectedTeamIds,
    selectedTeamId,  setSelectedTeamId,
    selectedUserIds, setSelectedUserIds,

    // Sonuc
    distribution,
    loadingJobs,
  };
};
